
import Vue from "vue";
import AppError from "@/shared/components/AppError.vue";
import TitledSection from "@/shared/components/TitledSection.vue";
import NoteSection from "@/features/Notes/NoteSection.vue";
import { NoteType } from "@/features/Notes/Note";
import SupplierEvent, { SupplierEventPublishedState, SupplierEventStatus, UISupplierEventStatus, SupplierEventType, UISupplierEventType } from "@/features/SupplierEvents/SupplierEvent";
import Country, { ExtendedCountry } from "@/features/SupplierEvents/Country";
import { formatDateWithTimestamp, formatDatepickerDate, generateUUID } from "@/shared/utils/common_utils";
import LabeledItem from "@/shared/components/LabeledItem.vue";
import FileUpload from "@/features/Documents/FileUpload.vue";
import FileList, { FileListDocumentType } from "@/features/Documents/FileList.vue";
import UploadedPlatformDocumentMetadata from "@/features/PlatformDocuments/UploadedPlatformDocumentMetadata";
import SupplierEventProduct from "@/features/SupplierEvents/SupplierEventProduct";
import SupplierEventProductList from "@/features/SupplierEvents/SupplierEventProductList.vue";
import SupplierEventRequestModel from "@/features/SupplierEvents/RequestModels/SupplierEventRequestModel";

export default Vue.extend({
    name: "SupplierEventPage",
    components: {
        AppError,
        TitledSection,
        LabeledItem,
        FileUpload,
        FileList,
        NoteSection,
        SupplierEventProductList,
    },
    data: () => ({
        dataSupplierEventID: null as string | null,
        supplierEvent: null as SupplierEvent | null,
        dataSupplierID: null as string | null,
        editMode: false,
        errorMessage: "",
        showError: false,
        loading: false,
        supplierEventTypes: Object.values(UISupplierEventType)
            .filter(v => !isNaN(Number(v)))
            .filter(v => v != UISupplierEventType["Logistics Event"])
            .map(value => ({ id: value, text: UISupplierEventType[Number(value)] })),
        countries: [] as ExtendedCountry[],
        SupplierEventType,
        UISupplierEventType,
        SupplierEventStatus,
        UISupplierEventStatus,
        priceEvent: SupplierEventType.PriceEvent,
        startDateMenu: false,
        endDateMenu: false,
        supplierEventValid: false,
        requestInProgress: false,
        documentWorkspaceID: null as string | null,
        eventTypeRules: [(v: SupplierEventType) => v != SupplierEventType.LogisticsEvent || "Choose a different type", (v: number) => !isNaN(v) || "Choose type..."],
        dateRules: [(v: string) => (v || "").length > 0 || "Select a date", (v: string) => !isNaN(Date.parse(v)) || "Invalid date"] as ((v: string) => boolean | string)[],
        reasonRules: [(v: string) => (v || "").length > 0 || "Reason is a required field"],
        plantLocationRules: [(v: string) => (v || "").length > 0 || "Plant location is a required field"],
        leadTimeRules: [(v: number) => !!v || "Impact on lead time is a required field"],
        isPriceEvent: false,
        noteType: NoteType,
        fileListDocumentType: FileListDocumentType,
    }),
    props: {
        supplierID: {
            type: String,
            required: true,
        },
        supplierEventID: {
            type: String,
            required: false, // Should be provided when loading existing supplier event, left blank when creating new supplier event
        },
    },
    computed: {
        canEditSupplierEvent(): boolean {
            if (this.supplierEvent!.publishedState != SupplierEventPublishedState.InProgress) {
                return false;
            }

            return this.$supplierEventAuthorizationService.canModifySupplierEvent(this.supplierEvent!);
        },
        validateForm(): boolean {
            var isValid = true;

            if (this.supplierEvent!.type == SupplierEventType.LogisticsEvent) {
                isValid = false;
            }

            if (this.supplierEvent!.type != SupplierEventType.PriceEvent) {
                if (this.supplierEvent!.startDate == null || this.supplierEvent!.endDate == null) {
                    isValid = false;
                } else if (this.supplierEvent!.startDate! > this.supplierEvent!.endDate!) {
                    isValid = false;
                }

                if (this.supplierEvent!.reason == null || this.supplierEvent!.reason == "") {
                    isValid = false;
                }

                if (this.supplierEvent!.plantLocation == null || this.supplierEvent!.plantLocation == "") {
                    isValid = false;
                }
            }

            return isValid;
        },
    },
    watch: {
        // When validity state changes, update the form's validity state
        incidentValid(isValid: boolean) {
            if (!isValid) {
                // update display to show invalid fields
                (this.$refs.supplierEventForm as any).validate();
            }
        },
        supplierEventID(newValue: string) {
            if (newValue) {
                this.getSupplierEvent(newValue);
            }
        },
    },
    async mounted() {
        if (this.supplierEventID) {
            this.dataSupplierEventID = this.supplierEventID;
            this.dataSupplierID = this.supplierID;

            this.editMode = false;
            await this.getSupplierEvent(this.dataSupplierEventID);

            this.$readStateService.markSupplierEventAsRead(this.dataSupplierEventID);
        } else {
            this.editMode = true; // Automatically in edit mode for a new supplier event
            this.supplierEvent = new SupplierEvent();
            this.supplierEvent.status = SupplierEventStatus.New;
            this.supplierEvent.publishedState = SupplierEventPublishedState.InProgress;
            this.supplierEvent.type = SupplierEventType.PriceEvent;
            this.supplierEvent.supplierID = this.supplierID;
        }

        this.isPriceEvent = this.supplierEvent?.type === SupplierEventType.PriceEvent;

        this.countries = (await this.$supplierEventService.getCountries()).map(c => new ExtendedCountry(c));
        this.documentWorkspaceID = generateUUID();
    },
    methods: {
        getSupplierEvent: async function (id: string) {
            try {
                this.loading = true;
                this.supplierEvent = await this.$supplierEventService.getSupplierEvent(id);
            } catch (e) {
                this.errorMessage = "Failed to fetch supplier event data";
                this.showError = true;
                console.error(e);
            } finally {
                this.loading = false;
            }
        },
        saveSupplierEvent: async function () {
            this.dateRules.push((v: string) => new Date(v) >= new Date(this.supplierEvent!.startDate!) || "End date can't come before start date");

            if ((this.$refs.supplierEventForm as any).validate() && this.validateForm) {
                try {
                    if (this.dataSupplierEventID) {
                        await this.$supplierEventService.updateSupplierEvent(this.dataSupplierEventID, new SupplierEventRequestModel(this.supplierEvent!));
                        this.editMode = false;
                    } else {
                        this.dataSupplierEventID = await this.$supplierEventService.createSupplierEvent(new SupplierEventRequestModel(this.supplierEvent!));
                        await this.getSupplierEvent(this.dataSupplierEventID); // Refresh with created supplier event from server
                        this.editMode = false;
                    }
                    this.$emit("supplier-event-updated");
                } catch (e) {
                    this.errorMessage = "Failed to save supplier event";
                    this.showError = true;
                }
            }
            if (this.supplierEvent && this.supplierEvent.countryID != null) {
                const country = this.countries.find(c => c.id === this.supplierEvent!.countryID!);
                if (country != null) {
                    this.supplierEvent!.country = country;
                }
            }
        },
        async editSupplierEvent() {
            this.editMode = true;
        },
        async cancelSupplierEvent(): Promise<void> {
            this.$emit("close-pane");
        },
        formatDateWithTimestampForDisplay(dateString: string) {
            if (dateString === null) return "";
            const date = new Date(dateString);
            return formatDateWithTimestamp(date);
        },
        formatDatepickerDateForDisplay(dateString: string) {
            if (dateString === null) return "";
            const date = new Date(dateString);
            return formatDatepickerDate(date);
        },
        formatCountryNameForDisplay(country: Country) {
            if (country === null) return "";
            return new ExtendedCountry(country).fullName;
        },
        documentUploaded(uploadedPlatformDocumentMetadata: UploadedPlatformDocumentMetadata): void {
            if (!this.supplierEvent!.uploadedPlatformDocumentMetadatas) {
                this.supplierEvent!.uploadedPlatformDocumentMetadatas = [];
            }
            this.supplierEvent!.uploadedPlatformDocumentMetadatas.push(uploadedPlatformDocumentMetadata);
        },
        documentRemoved(removedDocumentMetadataID: string): void {
            this.supplierEvent!.uploadedPlatformDocumentMetadatas = this.supplierEvent!.uploadedPlatformDocumentMetadatas.filter(
                updm => updm.uploadedPlatformDocument.documentID != removedDocumentMetadataID
            );
        },
        uploadChanged(uploadInProgress: boolean) {
            this.requestInProgress = uploadInProgress;
        },
        onProductAdded(product: SupplierEventProduct) {
            if (this.dataSupplierEventID) {
                product.supplierEventID = this.dataSupplierEventID;
            } else {
                product.supplierEventID = "";
            }
            this.supplierEvent?.products?.push(product);
        },
        onProductsAdded(products: SupplierEventProduct[]) {
            products.forEach(sep => this.onProductAdded(sep));
        },
        onProductUpdated(product: SupplierEventProduct) {
            const index = this.supplierEvent?.products.findIndex(p => p.id === product.id);
            if (this.supplierEvent?.products != null && index !== undefined) {
                Vue.set(this.supplierEvent.products, index, product);
            }
            if (index != null && this.supplierEvent?.products != null) {
                this.supplierEvent.products[index] = product;
            }
        },
        onProductDeleted(id: string) {
            const index = this.supplierEvent?.products.findIndex(p => p.id === id);
            if (index !== undefined) {
                this.supplierEvent?.products?.splice(index, 1);
            }
        },
        onNoteAdded() {
            this.getSupplierEvent(this.supplierEventID);
        },
        onSupplierEventTypeChange() {
            this.isPriceEvent = this.supplierEvent?.type === SupplierEventType.PriceEvent;
        },
    },
});
